import { formatForDisplay } from '@kickback/contracts';
import { decodeMoney, type MoneyWire } from '../api/client.js';

/**
 * The only place money is turned into text.
 *
 * The wire value is decoded to a bigint-backed `Money` and formatted by the
 * shared codec, so the client and the server agree on what "$86.87" means. No
 * component divides by 100, and `toFixed` appears nowhere — that was how the
 * prototype ended up doing float arithmetic on money in the browser.
 */
export function Money({
  value,
  className,
}: {
  value: MoneyWire | undefined;
  className?: string;
}) {
  if (value === undefined) return <span className={className}>—</span>;

  const money = decodeMoney(value);
  const negative = money.cents < 0n;

  return (
    <span className={[className, negative ? 'money--negative' : undefined].filter(Boolean).join(' ')}>
      {formatForDisplay(money)}
    </span>
  );
}

/** A labelled figure. The label is what makes a number mean something. */
export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: MoneyWire | undefined;
  /**
   * `| undefined` explicitly, as `value` above already is.
   *
   * Under `exactOptionalPropertyTypes`, `hint?: string` means "absent, or a
   * string" and rejects an explicit `undefined`. Callers derive the hint from
   * data that may not have loaded, so `hint={loaded ? … : undefined}` is the
   * natural way to write it, and forcing a conditional spread at every call
   * site to express the same thing buys nothing.
   */
  hint?: string | undefined;
  tone?: 'positive' | 'muted' | undefined;
}) {
  return (
    <div className={`stat${tone ? ` stat--${tone}` : ''}`}>
      <div className="stat__label">{label}</div>
      <div className="stat__value">
        <Money value={value} />
      </div>
      {hint !== undefined && <div className="stat__hint">{hint}</div>}
    </div>
  );
}
